// =============================================================================
// TrendBar.tsx
// Single bar in the monthly spend trend chart.
//
// Visual encoding (WS2 sparse-data fix):
//  - Dominant month (highest non-zero spend): full opacity + $ value above
//  - Other months with spend > 0: 0.4 opacity, no label
//  - Zero-spend months: 1px faint baseline tick so the user can distinguish
//    "this month had no spend" from "no data was loaded"
// =============================================================================

import React, { useEffect, useState } from 'react';

interface TrendBarProps {
  spend: number;
  maxSpend: number;
  isDominant: boolean;
  label: string;
  valueLabel: string;
  delay: number;
}

export function TrendBar({
  spend, maxSpend, isDominant, label, valueLabel, delay,
}: TrendBarProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 100 + delay); return () => clearTimeout(t); }, [delay]);

  const proportional = (spend / Math.max(maxSpend, 1)) * 50;
  const targetH = spend > 0 ? Math.max(4, proportional) : 1;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {isDominant && (
        <span style={{ fontSize: 10, fontWeight: 600, opacity: mounted ? 1 : 0, transition: 'opacity 0.3s ease' }}>{valueLabel}</span>
      )}
      <div style={{
        width: '100%',
        height: mounted ? targetH : (spend > 0 ? 4 : 1),
        background: spend > 0 ? 'var(--accent)' : 'var(--text3)',
        borderRadius: 2,
        opacity: spend > 0 ? (isDominant ? 1 : 0.4) : 0.3,
        transition: 'height 0.7s cubic-bezier(0.34,1.56,0.64,1)',
      }} />
      <span style={{ fontSize: 10, color: isDominant ? 'var(--text)' : 'var(--text3)' }}>{label}</span>
    </div>
  );
}
